document.addEventListener('alpine:init', () => {
    Alpine.data('cartGwp', () => ({
        rules: [],

        /**
         * Initializes the GWP component.
         * Reads rules from <script data-gwp-rule> children and registers
         * itself as a post-processor on the cart store so reconciliation
         * runs (and is awaited) inside every cart mutation, keeping the
         * display snapshot frozen until gifts have settled.
         * @returns {void}
         */
        init() {
            this.readRules();

            Alpine.store('cart').registerPostProcessor((ctx) => this.reconcile(0, ctx));

            this.$nextTick(() => this.reconcile());
        },

        /**
         * Reads the GWP rules from <script data-gwp-rule> children of $el.
         * @returns {void}
         */
        readRules() {
            this.rules = [];

            this.$el.querySelectorAll('script[data-gwp-rule]').forEach((el) => {
                let parsed;
                try {
                    parsed = JSON.parse(el.textContent);
                } catch (e) {
                    console.error('[cartGwp] invalid rule JSON', e);
                    return;
                }

                if (!parsed || !Array.isArray(parsed.variants) || !parsed.variants.length) return;

                this.rules.push({
                    id: parsed.id || '',
                    scope: parsed.scope === 'product' ? 'product' : 'all',
                    targetProductIds: Array.isArray(parsed.targetProducts) ? parsed.targetProducts : [],
                    variantIds: parsed.variants,
                });
            });
        },

        /**
         * Determines if a rule passes for a given set of subscription lines.
         * @param {Object} rule - Normalized rule.
         * @param {Array}  subLines - Subscription cart lines.
         * @returns {boolean}
         */
        rulePasses(rule, subLines) {
            if (!subLines.length) return false;
            if (rule.scope === 'all') return true;
            if (rule.scope === 'product') {
                if (!rule.targetProductIds.length) return false;
                return subLines.some((item) => rule.targetProductIds.includes(item.product_id));
            }
            return false;
        },

        /**
         * Reads a line item property. Handles both object and array shapes.
         * @param {Object} item - Cart line item.
         * @param {string} key - Property key.
         * @returns {string|null}
         */
        getProp(item, key) {
            const props = item?.properties;
            if (!props) return null;

            if (!Array.isArray(props)) {
                return props[key] ?? null;
            }

            const found = props.find((p) => (p.key || p.name) === key);
            return found?.value ?? null;
        },

        /**
         * Reconciles managed gift lines against the current cart + rules.
         * When multiple rules match, only the most specific applies
         * (product-scoped over all-scoped); only that rule's gifts are desired.
         * Invoked as a cart-store post-processor inside every cart mutation
         * (and once on init) so the resulting add/remove gift fetches are
         * awaited before the display snapshot is released.
         * @param {number} [depth=0] - Recursion guard for rebuy cleanup pass.
         * @param {Object} [ctx]
         * @param {boolean} [ctx.skipSections] - Skip cart section HTML (DTC).
         * @returns {Promise<void>}
         */
        async reconcile(depth = 0, ctx = {}) {
            if (!this.rules.length) return;

            const items = Alpine.store('cart').items || [];

            try {
                const rebuyGifts = items.filter((item) =>
                    this.getProp(item, '_source') === 'Rebuy'
                );

                if (rebuyGifts.length) {
                    await this.removeGifts(rebuyGifts, ctx);
                    if (depth < 1) {
                        return this.reconcile(depth + 1, ctx);
                    }
                    return;
                }

                const subLines = items.filter((item) =>
                    item.selling_plan_allocation &&
                    this.getProp(item, '_isFreeGift') !== 'true'
                );

                const managed = new Set();
                this.rules.forEach((rule) => {
                    rule.variantIds.forEach((id) => managed.add(id));
                });

                // Most specific matching rule wins: product-scoped over all-scoped.
                // Only that block's gift_products are desired (no union across rules).
                const matching = this.rules.filter((rule) => this.rulePasses(rule, subLines));
                const winner = matching.find((rule) => rule.scope === 'product')
                    || matching.find((rule) => rule.scope === 'all')
                    || null;

                const desired = new Set(winner ? winner.variantIds : []);

                const giftsInCart = items.filter((item) =>
                    this.getProp(item, '_isFreeGift') === 'true'
                );
                const giftVariantsInCart = new Set(
                    items
                        .filter((item) =>
                            managed.has(item.variant_id) &&
                            (this.getProp(item, '_isFreeGift') === 'true' ||
                                this.getProp(item, '_source') === 'Rebuy')
                        )
                        .map((i) => i.variant_id)
                );

                const toAdd = [];
                desired.forEach((id) => {
                    if (!giftVariantsInCart.has(id)) toAdd.push(id);
                });

                const toRemoveManaged = giftsInCart.filter((item) =>
                    managed.has(item.variant_id) &&
                    !desired.has(item.variant_id) &&
                    this.getProp(item, '_source') === 'cartGwp'
                );

                // Gifts added from the PDP share `_source: cartGwp` but may not
                // be in any rule's managed set; page-only offers (SK Offer Box)
                // use `_source: ctrSkOfferBox` so the rules above never prune
                // them. Clear both kinds once no qualifying subscription parent
                // remains in the cart.
                const orphanSources = ['cartGwp', 'ctrSkOfferBox'];
                const toRemoveOrphans = subLines.length
                    ? []
                    : giftsInCart.filter(
                          (item) =>
                              orphanSources.includes(this.getProp(item, '_source')) &&
                              !toRemoveManaged.includes(item)
                      );

                const toRemove = [...toRemoveManaged, ...toRemoveOrphans];

                if (!toAdd.length && !toRemove.length) return;

                if (toAdd.length) await this.addGifts(toAdd, ctx);
                if (toRemove.length) await this.removeGifts(toRemove, ctx);
            } catch (e) {
                console.error('[cartGwp]', e);
            }
        },

        /**
         * Adds gift variants to the cart and refreshes the cart store.
         * @param {number[]} variantIds
         * @param {Object} [ctx]
         * @param {boolean} [ctx.skipSections]
         * @returns {Promise<void>}
         */
        async addGifts(variantIds, ctx = {}) {
            const cart = Alpine.store('cart');
            const skipSections = Boolean(ctx.skipSections);

            try {
                const body = {
                    items: variantIds.map((id) => ({
                        id,
                        quantity: 1,
                        properties: { _isFreeGift: 'true', _source: 'cartGwp' },
                    })),
                };
                if (!skipSections) body.sections = cart.refreshSections;

                const response = await fetch('/cart/add.js', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(body),
                });
                const data = await response.json();
                // DTC (skipSections) merges the added lines locally instead of
                // waiting on a /cart.js read before the checkout redirect.
                await cart.applyCartResponse(data, { optimistic: skipSections });
            } catch (error) {
                console.error('[cartGwp] addGifts failed', error);
            }
        },

        /**
         * Removes managed gift lines from the cart and refreshes the cart store.
         * @param {Array} giftItems - Cart line items to remove (by key).
         * @param {Object} [ctx]
         * @param {boolean} [ctx.skipSections]
         * @returns {Promise<void>}
         */
        async removeGifts(giftItems, ctx = {}) {
            const cart = Alpine.store('cart');
            const skipSections = Boolean(ctx.skipSections);
            const updates = {};
            giftItems.forEach((item) => {
                updates[item.key] = 0;
            });

            try {
                const body = { updates };
                if (!skipSections) body.sections = cart.refreshSections;

                const response = await fetch('/cart/update.js', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(body),
                });
                const data = await response.json();
                await cart.applyCartResponse(data);
            } catch (error) {
                console.error('[cartGwp] removeGifts failed', error);
            }
        },
    }));
});